import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Case } from '../lib/database';

// CaseContext is populated by routing/CaseShell once the current case has
// been loaded from the :caseKey URL param. All downstream screens read the
// Case via useCase() instead of taking it as a prop.
//
// The shell guarantees the value is non-null by the time any child renders,
// so useCase() returns Case directly (not Case | null). Call useOptionalCase()
// if you're outside the shell (very rare).

interface CaseContextValue {
  caseData: Case;
  reloadCase: () => void;
}

const CaseContext = createContext<CaseContextValue | null>(null);

interface CaseProviderProps {
  caseData: Case;
  /** Re-read the case from the API. The shell loads it ONCE, so a screen that
   *  writes a `cases` column leaves every reader of `caseData` stale — #1105's
   *  setup panel writes `docket` / `judge`, and #1093's matrix header renders
   *  exactly those two a few lines above the form that changed them.
   *
   *  Optional so the many tests that mount `<CaseProvider caseData={…}>`
   *  directly keep working; it no-ops there, which is correct. */
  reloadCase?: () => void;
  children: ReactNode;
}

// Outside the shell — unit tests, stories — there is no case to re-read, so
// doing nothing is correct rather than a missing implementation.
// eslint-disable-next-line @typescript-eslint/no-empty-function
const NO_RELOAD = () => {};

export function CaseProvider({ caseData, reloadCase, children }: CaseProviderProps) {
  // Memoised so the context value's identity tracks its contents. A fresh
  // object every render makes `useReloadCase()` return a new function every
  // render even when the shell passed a stable one, which breaks any consumer
  // that depends on it. `NO_RELOAD` is module-level for the same reason.
  const value = useMemo(
    () => ({ caseData, reloadCase: reloadCase ?? NO_RELOAD }),
    [caseData, reloadCase],
  );
  return (
    <CaseContext.Provider value={value}>
      {children}
    </CaseContext.Provider>
  );
}

/** Re-read the current case. **Stable across renders** — safe to put in a
 *  dependency array, and `CaseShell` holds it in a `useCallback` so that it is.
 *  Callers depend on that property, so do not inline a fresh closure at the
 *  provider. See `CaseProviderProps.reloadCase`. */
export function useReloadCase(): () => void {
  return useContext(CaseContext)?.reloadCase ?? NO_RELOAD;
}

export function useCase(): Case {
  const ctx = useContext(CaseContext);
  if (!ctx) {
    throw new Error(
      'useCase() must be rendered inside a CaseProvider (routing/CaseShell).',
    );
  }
  return ctx.caseData;
}

export function useOptionalCase(): Case | null {
  return useContext(CaseContext)?.caseData ?? null;
}
